import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import { ConsumerService } from "../../../services/api/consumer.service";
import { ConsumerActions } from "../../actions/consumer.actions";
import { ConsumerAchActions } from "../../actions/consumer-ach.actions";
import { ConsumerAdminActions } from "../../actions/consumer-admin.actions";
import { OrderProposalActions } from "../../actions/order-proposal.actions";
import { FailState } from "../../models/fail-state";
import { FundingBalanceState } from "../../models/funding-balance-state";
import { AppState } from "../../reducers";
import { ConsumerSelectors } from "../../selectors/consumer.selectors";

@Injectable()
export class FundingBalanceGetEffects {
	get = createEffect(() =>
		this.actions.pipe(
			ofType(ConsumerActions.fundingBalance.get),
			withLatestFrom(this.store.select(ConsumerSelectors.fundingBalance)),
			switchMap(([, state]) =>
				FundingBalanceGetEffects.canUseStoreValue(state)
					? of(ConsumerActions.fundingBalance.getSuccess({ ...state, loading: false }))
					: of(ConsumerActions.fundingBalance.getApi())
			)
		)
	);

	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(
				ConsumerActions.fundingBalance.getApi,
				ConsumerAchActions.funded,
				ConsumerAdminActions.creditCard.chargeSuccess,
				OrderProposalActions.acceptProposal.acceptSuccess
			),
			switchMap(() => {
				return this.consumerService.fundingBalance().pipe(
					map((result) => {
						const balance = _.get(result, "balance", "0") || "0";
						const currency = _.get(result, "currency", "USD") || "USD";
						const fundingBalance = {
							balance: parseFloat(balance),
							currency,
							loading: false
						};
						return ConsumerActions.fundingBalance.getSuccess(fundingBalance);
					}),
					catchError((error) =>
						of(ConsumerActions.fundingBalance.getFail(new FailState(error)))
					)
				);
			})
		)
	);

	constructor(
		private actions: Actions,
		private store: Store<AppState>,
		private consumerService: ConsumerService
	) {}

	private static canUseStoreValue(state: FundingBalanceState): boolean {
		return !_.has(state, "error") && !state.loading;
	}
}
